import { useTranslation } from "@msuite/ui/localization";
import { Tag } from "@msuite/ui/tag";
import { guardString } from "@msuite/utils/strings";
import React from "react";
import type { CrewDefinitionListItem } from "@/hooks/use-crew-definitions";

/** Props Interface */
interface CrewDefinitionTagsProps {
	crewDefinition: CrewDefinitionListItem;
}

/**
 * What a crew is, in one row of tags: the partner it stands for, or how many people make it up and
 * who runs it.
 *
 * A partner's crew shows the partner and nothing else, because the partner staffs it — the member
 * list is empty by design and a "0 members" tag beside the partner's name would read as a fault.
 */
export function CrewDefinitionTags({ crewDefinition }: CrewDefinitionTagsProps) {
	/** Hooks */
	const { t } = useTranslation();

	/** Functions */
	function getLeadName(): string | null {
		for (const member of crewDefinition._members) {
			if (member.isLead) return member.user?.name ?? null;
		}
		return null;
	}

	/** Computed */
	const leadName = getLeadName();

	/** Render */
	return (
		<div className="h-stack flex-wrap gap-1">
			{crewDefinition.partnerOrgId ? (
				<Tag color="blue">{guardString(crewDefinition._partnerOrganization?.name)}</Tag>
			) : (
				<React.Fragment>
					<Tag color="gray">
						{t("planning.crews.crew_member_count", {
							count: String(crewDefinition._members.length),
						})}
					</Tag>
					{leadName && <Tag color="accent">{guardString(leadName)}</Tag>}
				</React.Fragment>
			)}
		</div>
	);
}
